import { Component, Inject, Optional } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { formatISO9075 } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import { of, Subscription, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { labelMaxLength, labelMinLength } from '../../app.constants';
import { ConfigService } from '../../config.service';
import { CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { ProjectsService } from '../../projects/projects.service';
import { isLabEdition } from '../../shared/container.utils';
import { TIMEZONES } from '../../shared/date.utils';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { Option } from '../../shared/models/option.model';
import { generateElementId } from '../../shared/string.utils';

interface DialogData {
  projectId: string;
}
@Component({
  host: {
    'class': 'mat-dialog-component',
    'test-id': 'create-session-dialog',
  },
  selector: 'create-session-dialog',
  styleUrls: ['./create-session-dialog.component.scss'],
  templateUrl: './create-session-dialog.component.html',
})
export class CreateSessionDialog {
  elementId = generateElementId();
  form: FormGroup;
  labRestriction = false;
  groups = [];
  timezoneOptions: Option[] = TIMEZONES.map(tz => ({ label: tz, value: tz }))


  private subscriptions = {
    projectsService: Subscription.EMPTY,
  }

  constructor(
    private configService: ConfigService,
    @Inject(MAT_DIALOG_DATA) @Optional() private data: DialogData,
    private dialog: MatDialogRef<CreateSessionDialog>,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private projectsService: ProjectsService,
  ) {
    // trim off the seconds
    const now = formatISO9075(new Date()).slice(0, -3);

    this.form = new FormGroup({
      projectSelect: new FormControl(this.data.projectId || '', [Validators.required]),
      dateTime: new FormControl(now, [Validators.required]),
      timezone: new FormControl(Intl.DateTimeFormat().resolvedOptions().timeZone || ''),
      subjectLabel: new FormControl('', [Validators.required, Validators.maxLength(labelMaxLength), Validators.minLength(labelMinLength)]),
      sessionLabel: new FormControl('', [Validators.required, Validators.maxLength(labelMaxLength), Validators.minLength(labelMinLength)]),
    });

    this.subscriptions.projectsService = this.projectsService.getGroupedProjects([PermissionAction.ContainersCreateHierarchy])
      .subscribe(groupedProjects => {
        this.groups = groupedProjects.map(group => ({
          ...group,
          projects: group.projects.filter(project => {
            if (this.configService.isFeatureEnabled('multiproject') && !isLabEdition(project)) {
              if (this.data.projectId === project._id) {
                this.labRestriction = true;
              }
              return false;
            }
            return true;
          }),
        }));
      });
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  submit(): void {
    this.coreApi.subjects.post({
      project: this.form.controls.projectSelect.value,
      code: this.form.controls.subjectLabel.value,
      label: this.form.controls.subjectLabel.value,
    }).pipe(
      catchError(error => {
        if (error.status === 409) {
          return of(null);
        }
        return throwError(error);
      }),
      switchMap(() => {
        const payload = {
          timestamp: this.getTimeStamp(),
          timezone: this.form.controls.timezone.value || 'UTC',
          label: this.form.controls.sessionLabel.value,
          subject: {
            code: this.form.controls.subjectLabel.value,
          },
          project: this.form.controls.projectSelect.value,
        };
        return this.coreApi.sessions.post(payload);
      })
    ).subscribe({
      next: (resp) => {
        this.notifications.success('session.add.success');
        this.dialog.close(resp);
      },
      error: (error) => {
        this.notifications.error('session.add.error');
        this.logger.error(error);
      },
    });
  }

  getLabelId(tag: string): string {
    return `${this.elementId}-${tag}`;
  }

  getTimeStamp(): string {
    const rawTimestamp = new Date(this.form.controls.dateTime.value);
    const timestamp = zonedTimeToUtc(rawTimestamp, this.form.controls.timezone.value || 'UTC');
    return timestamp.toISOString();
  }
}
